/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { IRecord } from "../../types/schema"
import { KeyValueList } from "../KeyValueList"
import { HoverDepartmentLoader } from "./HoverDepartmentLoader"
import { Button, Grid, Icon, Spacer } from "@cd2/components/build/es6"
import { EmployeeAvatar } from "../../pages/user/Rota/EmployeeAvatar"
import { TextWithNewLines } from "../TextWithNewLines"
import { createHoverLoader } from "./createHoverLoader"
import { Flex } from "../Flex"
import { Pill } from "../Pill"
import { useDialogueConfirmation2 } from "../../hooks/useDialogConfirmation"
import { TextGroupMedium } from "../Title"
import { UserManageEmployeesFlowTagsTab } from "../../pages/user/UserManageEmployeePage/UserManageEmployeesFlowTagsTab"
import { buildRoute, eRoutes } from "../../routes"
import { Link } from "react-router-dom"
import { QuickToolTip } from "../QuickTooltip"
import { icons } from "../../utils/icons"
import { siteColors } from "../../providers/ThemeProvider"

type IEmployee = IRecord<"employees", "firstName" | "lastName" | "email" | "jobTitle" | "notes"> & {
  company: IRecord<"companies">
  location: IRecord<"locations", "name"> | null
  departments: IRecord<"departments">[]
  employeeTags: IRecord<"employeeTags", "name">[]
  hasOutstandingAbsence: boolean
}

export const HoverEmployeeLoader = createHoverLoader<
  IEmployee,
  { noCircle?: boolean; targetFontSize?: number }
>({
  model: "employees",
  attributes: [
    "firstName",
    "lastName",
    "email",
    "jobTitle",
    "departments",
    "notes",
    "location",
    "company",
    { name: "employeeTags", attributes: ["name"], filter: { matches: { archived: false } } },
    {
      as: "hasOutstandingAbsence",
      value: { exists: { attr: { name: "absences", filter: { matches: { outstanding: true } } } } },
    },
  ],
  noHoverIcon: true,
  Target({ record, isTitle, other }) {
    return (
      <Flex gap={4} alignItems="center">
        {!other.noCircle && <EmployeeAvatar employee={record} />}
        {other.noCircle && <Icon icon={icons.hoverLoader} color="#333" size={8} />}
        <span
          css={css`
            white-space: normal;
            ${other.targetFontSize ? `font-size: ${other.targetFontSize}px;` : ""}
          `}
        >
          {record.firstName} {record.lastName}
        </span>
        {!isTitle && record.hasOutstandingAbsence && (
          <QuickToolTip content="This employee has ongoing absences">
            <Icon icon={icons.warning} color={siteColors.orange} />
          </QuickToolTip>
        )}
      </Flex>
    )
  },
  viewLink: (record, globalState) => {
    if (globalState.inAdminArea) {
      return buildRoute(eRoutes["/companies/:companyId/employees/:id"], {
        id: record.id,
        companyId: record.company.id,
      })
    } else if (window.location.href.includes("companies")) {
      return buildRoute(eRoutes["/companies/:companyId/employees/:id"], {
        id: record.id,
        companyId: record.company.id,
      })
    } else if (globalState.user.isManager || globalState.user.isAdmin) {
      return buildRoute(eRoutes["/manager/employees/:id"], record)
    } else if (globalState.permissions.canManageEmployees) {
      return buildRoute(eRoutes["/manager/employees/:id"], record)
    }
    return null
  },
  HoverContent({ record }) {
    const triggerViewTagHistory = useViewEmployeeTagHistory()
    return (
      <div>
        {record.hasOutstandingAbsence && (
          <div>
            <div
              css={css`
                background: ${siteColors.pills.orange};
                border-radius: 4px;
                padding: 4px;
              `}
            >
              This employee has ongoing absences
            </div>
            <div
              css={css`
                text-align: right;
              `}
            >
              <Link
                to={buildRoute(eRoutes["/manager/employees/:id"], record, {
                  qs: { pageTab: "absences" },
                })}
              >
                View absences
              </Link>
            </div>
          </div>
        )}
        <Spacer size={8} />
        <KeyValueList
          data={[
            { label: "Email", value: record.email },
            { label: "Job title", value: record.jobTitle },
            { label: "Location", value: record.location?.name ?? "-" },
            {
              label: `Departments (${record.departments.length})`,
              value: (
                <Grid gap={4}>
                  {record.departments.length === 0 && <i>- no departments -</i>}
                  {record.departments.map(department => (
                    <HoverDepartmentLoader key={department.id} id={department.id} />
                  ))}
                </Grid>
              ),
            },
            {
              label: "Employee Tags",
              value: (
                <div>
                  <Flex gap={4} wrap>
                    {record.employeeTags.map(tag => (
                      <Pill key={tag.id}>{tag.name}</Pill>
                    ))}
                  </Flex>
                  <Spacer size={4} />
                  <Button
                    extendTheme={{
                      padding: { topAndBottom: 6, sides: 8 },
                      fontSize: 12,
                    }}
                    onClick={() => triggerViewTagHistory(record)}
                  >
                    Tag History
                  </Button>
                </div>
              ),
            },

            {
              label: "Notes",
              value: record.notes ? <TextWithNewLines text={record.notes} /> : "-",
            },
          ]}
        />
      </div>
    )
  },
})

export function useViewEmployeeTagHistory() {
  return useDialogueConfirmation2<void, { id: string }>({
    onlyDestroyOnResolve: true,
    Component({ dialogData, registerOutsideClick, onResolve }) {
      registerOutsideClick(() => onResolve())
      return (
        <div>
          <TextGroupMedium heading="Employee Tag History" />
          <Spacer size={16} />
          <UserManageEmployeesFlowTagsTab employeeId={dialogData.id} historyOnly />
        </div>
      )
    },
  })
}
